import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef } from 'react';

/**
 * Entry stored in the title cache
 */
type TitleCacheEntry = {
    title: ReactNode;
    timestamp: number;
    routeId: string;
};

/**
 * Context value providing low-level cache operations
 */
type TitleCacheContextValue = {
    /** Get a cached title by key, returns null if not found or stale */
    getTitle: (key: string) => ReactNode | null;
    /** Store a title in the cache with the current timestamp */
    setTitle: (key: string, title: ReactNode) => void;
    /** Invalidate cache entries, optionally matching a pattern */
    invalidate: (pattern?: string) => void;
    /** Check if a valid (non-stale) cache entry exists for a key */
    has: (key: string) => boolean;
    /**
     * Resolve an async title through the cache: returns the cached value when fresh, joins an
     * in-flight resolution for the same key (deduplication), and otherwise runs `resolver` and
     * caches its result. Rejections are not cached.
     */
    resolveTitle: (key: string, resolver: () => Promise<ReactNode>) => Promise<ReactNode>;
};

const TitleCacheContext = createContext<TitleCacheContextValue | null>(null);

interface TitleCacheProviderProps {
    children: ReactNode;
    staleTime?: number; // Default cache duration in ms
}

const DEFAULT_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Provider component that manages a global cache for page titles and breadcrumbs.
 * This provides the low-level cache storage that other hooks build upon.
 *
 * @param props.children - Child components that will have access to the cache
 * @param props.staleTime - How long cached entries remain valid (default: 5 minutes)
 *
 * @example
 * ```tsx
 * <TitleCacheProvider staleTime={10 * 60 * 1000}>
 *   <App />
 * </TitleCacheProvider>
 * ```
 */
export function TitleCacheProvider({ children, staleTime = DEFAULT_STALE_TIME }: TitleCacheProviderProps) {
    const cacheRef = useRef<Map<string, TitleCacheEntry>>(new Map());
    const pendingRef = useRef<Map<string, Promise<ReactNode>>>(new Map());

    const isStale = useCallback(
        (entry: TitleCacheEntry | undefined): boolean => {
            if (!entry) return true;
            return Date.now() - entry.timestamp > staleTime;
        },
        [staleTime],
    );

    const getTitle = useCallback(
        (key: string): ReactNode | null => {
            const entry = cacheRef.current.get(key);
            if (!entry || isStale(entry)) {
                return null;
            }
            return entry.title;
        },
        [isStale],
    );

    const setTitle = useCallback((key: string, title: ReactNode) => {
        cacheRef.current.set(key, {
            title,
            timestamp: Date.now(),
            routeId: key,
        });
    }, []);

    const has = useCallback(
        (key: string): boolean => {
            const entry = cacheRef.current.get(key);
            return entry !== undefined && !isStale(entry);
        },
        [isStale],
    );

    const invalidate = useCallback((pattern?: string) => {
        if (!pattern) {
            // Clear entire cache
            cacheRef.current.clear();
            pendingRef.current.clear();
        } else {
            // Clear entries matching pattern
            for (const key of cacheRef.current.keys()) {
                if (key.includes(pattern)) {
                    cacheRef.current.delete(key);
                    pendingRef.current.delete(key);
                }
            }
        }
    }, []);

    const resolveTitle = useCallback(
        (key: string, resolver: () => Promise<ReactNode>): Promise<ReactNode> => {
            const cached = getTitle(key);
            if (cached !== null) {
                return Promise.resolve(cached);
            }

            const pending = pendingRef.current.get(key);
            if (pending) {
                return pending;
            }

            const request = Promise.resolve()
                .then(resolver)
                .then(
                    (title) => {
                        setTitle(key, title);
                        pendingRef.current.delete(key);
                        return title;
                    },
                    (error) => {
                        pendingRef.current.delete(key);
                        throw error;
                    },
                );

            pendingRef.current.set(key, request);
            return request;
        },
        [getTitle, setTitle],
    );

    const value = useMemo(
        () => ({
            getTitle,
            setTitle,
            invalidate,
            has,
            resolveTitle,
        }),
        [getTitle, setTitle, invalidate, has, resolveTitle],
    );

    return <TitleCacheContext.Provider value={value}>{children}</TitleCacheContext.Provider>;
}

/**
 * Hook to access the shared title cache.
 * Provides cache storage operations (get, set, invalidate) plus `resolveTitle`, which resolves
 * async route titles through the cache with in-flight deduplication. This is the ONLY title
 * cache — do not layer additional caches on top of it.
 *
 * @returns Cache operations (getTitle, setTitle, invalidate, has, resolveTitle)
 * @throws Error if used outside of TitleCacheProvider
 *
 * @example
 * ```tsx
 * const storage = useTitleCacheStorage();
 *
 * // Get a cached title
 * const cachedTitle = storage.getTitle('breadcrumb:route-id');
 *
 * // Set a title in cache
 * storage.setTitle('breadcrumb:route-id', 'My Page Title');
 *
 * // Invalidate specific cache entries
 * storage.invalidate('breadcrumb:');
 *
 * // Clear entire cache
 * storage.invalidate();
 * ```
 */
export function useTitleCacheStorage() {
    const context = useContext(TitleCacheContext);
    if (!context) {
        throw new Error('useTitleCacheStorage must be used within TitleCacheProvider');
    }
    return context;
}
